import { restClient, NetworkErrorEventModel } from "../ajax";
import { getAccessToken } from "../authentication";
import store from "../store";
import { AppMutationTypes } from "../store/mutations";

/** Configure the REST client to automatically refresh the access token and handle API errors */
export default function configureAxios(): void {
    // Request interceptor: refresh the access token before each API call.
    // MSAL's acquireTokenSilent returns a cached token if still valid,
    // or uses the refresh token to get a new one automatically when it has expired.
    // The Authorization header is always set to the latest token to ensure it is never stale.
    // https://learn.microsoft.com/en-us/entra/msal/javascript/browser/token-lifetimes
    restClient.client.interceptors.request.use(async (config) => {
        const token = await getAccessToken();
        if (token) {
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    // Response interceptor: handle error responses
    // https://github.com/axios/axios#interceptors
    restClient.client.interceptors.response.use(undefined, error => {
        if (error.response) {
            if (error.response.status === 401) {
                // When a 401 response is found, remove the current user to prompt re-authentication.
                store.commit(AppMutationTypes.setUser, null);
            }
            const errorEvent = new CustomEvent<NetworkErrorEventModel>(NetworkErrorEventModel.EVENT_NAME, {
                detail: {
                    httpStatusCode: error.response.status,
                    message: error.message || "An error occurred while processing the request."
                }
            });

            window.dispatchEvent(errorEvent);
        }

        // Axios expects a returned rejected promise with the error information:
        return Promise.reject(error);
    });
}
